import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, map, shareReplay } from 'rxjs';

/**
 * Media queries mirroring the RDS breakpoint scale
 * (`@rds/core/styles/abstracts/variables/_breakpoints.scss`):
 * `s: 0px`, `m: 768px`, `l: 1024px`.
 *
 * The fractional upper bounds keep the `max-width` queries from overlapping the
 * `min-width` ones by a pixel, which is what the hand-written
 * `max-width: 767px` / `max-width: 768px` mix in the styles used to do.
 */
export const VIEWPORT_QUERY = {
  /** Phones — below the RDS `m` breakpoint. */
  mobile: '(max-width: 767.98px)',
  /** Tablets — from the RDS `m` breakpoint up to, but excluding, `l`. */
  tablet: '(min-width: 768px) and (max-width: 1023.98px)',
  /** Phones and tablets — everything below the RDS `l` breakpoint. */
  compact: '(max-width: 1023.98px)',
} as const;

/**
 * Single source of truth for viewport-driven layout decisions.
 *
 * Use this instead of injecting `BreakpointObserver` and repeating a media
 * query literal in each component. For styling, prefer the RDS `rds-media()`
 * mixin — reach for these signals only when the markup itself has to change.
 *
 * Each breakpoint is exposed both as a signal (for templates) and as an
 * observable (for rxjs pipelines). The observables emit their current value
 * synchronously on subscribe.
 */
@Injectable({
  providedIn: 'root',
})
export class ViewportService {
  readonly #breakpointObserver = inject(BreakpointObserver);

  /** Emits `true` below 768px. */
  public readonly isMobile$ = this.#observe(VIEWPORT_QUERY.mobile);

  /** Emits `true` between 768px and 1023.98px. */
  public readonly isTablet$ = this.#observe(VIEWPORT_QUERY.tablet);

  /** Emits `true` below 1024px, i.e. on both phones and tablets. */
  public readonly isCompact$ = this.#observe(VIEWPORT_QUERY.compact);

  /** `true` below 768px. */
  public readonly isMobile = this.#toSignal(
    this.isMobile$,
    VIEWPORT_QUERY.mobile
  );

  /** `true` between 768px and 1023.98px. */
  public readonly isTablet = this.#toSignal(
    this.isTablet$,
    VIEWPORT_QUERY.tablet
  );

  /** `true` below 1024px, i.e. on both phones and tablets. */
  public readonly isCompact = this.#toSignal(
    this.isCompact$,
    VIEWPORT_QUERY.compact
  );

  #observe(query: string): Observable<boolean> {
    return this.#breakpointObserver.observe(query).pipe(
      map(state => state.matches),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  #toSignal(source: Observable<boolean>, query: string): Signal<boolean> {
    return toSignal(source, {
      initialValue: this.#breakpointObserver.isMatched(query),
    });
  }
}
